import React, { useEffect, useMemo, useRef, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Slider, Tooltip } from "antd";
// FONT AWESOME
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faMinus, faPlus, faUpDown } from "@fortawesome/free-solid-svg-icons";
import elClose from "../../../assets/icons/elevatorSingle/el-close.svg";
import esStopped from "../../../assets/icons/escalator/es-dark-stopped.svg";
import movingWalkStopped from "../../../assets/icons/movingWalk/we-dark-stopped.svg";
import { LiftType } from "../../../constants/lifts";
import type {
  GroupWaypoint,
  MoveSession,
  Offset,
  ResizeHandle,
  ResizeSession,
  RotationTarget,
  RotationSession,
  Size,
  SvgControllerProps,
  Waypoint,
} from "./types";

const MIN_RESIZE_SIZE = 8;
const HANDLE_SIZE = 10;
const ROTATION_HANDLE_OFFSET = 24;
const NAME_LABEL_GAP = 6;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function resolveLiftType(value: unknown): LiftType | null {
  if (typeof value === "number") {
    if (
      value === LiftType.ELEVATOR ||
      value === LiftType.ESCALATOR ||
      value === LiftType.MOVINGWALK
    ) {
      return value;
    }
    return null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  if (Number.isFinite(parsed)) {
    return resolveLiftType(parsed);
  }

  const enumValue = (LiftType as unknown as Record<string, unknown>)[trimmed.toUpperCase()];
  return typeof enumValue === "number" ? (enumValue as LiftType) : null;
}

function SvgController({
  svgSrc,
  alt,
  showControls = true,
  minZoom = 0.5,
  maxZoom = 5,
  zoomStep = 0.2,
  waypoints = [],
  groupWaypoints = [],
  isPinMode = false,
  onPinWaypoint = undefined,
  onWaypointSelect = undefined,
  onGroupWaypointSelect = undefined,
  isLiftEditorOpen = false,
  isGroupEditorOpen = false,
  activeWaypointId = null,
  activeGroupWaypointId = null,
  onWaypointResize = undefined,
  onGroupWaypointResize = undefined,
  onWaypointMove = undefined,
  onGroupWaypointMove = undefined,
  onWaypointRotate = undefined,
  onGroupWaypointRotate = undefined,
  onViewportCenterChange = undefined,
  renderOverlay = undefined,
  onSvgLoaded = undefined,
}: SvgControllerProps) {
  const [zoom, setZoom] = useState<number>(1);
  const [zoomSpeed, setZoomSpeed] = useState<number>(zoomStep);
  const [isInteractingWithControls, setIsInteractingWithControls] = useState<boolean>(false);
  const [isPanningEnabled, setIsPanningEnabled] = useState<boolean>(true);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<Offset>({ x: 0, y: 0 });
  const [viewportSize, setViewportSize] = useState<Size>({ width: 1, height: 1 });
  const [imageNaturalSize, setImageNaturalSize] = useState<Size>({ width: 1, height: 1 });
  const [resizeSession, setResizeSession] = useState<ResizeSession | null>(null);
  const [moveSession, setMoveSession] = useState<MoveSession | null>(null);
  const [rotationSession, setRotationSession] = useState<RotationSession | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const suppressInteractionUntilRef = useRef<number>(0);

  const markControlInteraction = (durationMs = 250) => {
    suppressInteractionUntilRef.current = Date.now() + durationMs;
  };

  const isControlInteractionSuppressed = () => Date.now() < suppressInteractionUntilRef.current;

  const baseRect = useMemo(() => {
    const viewportWidth = viewportSize.width;
    const viewportHeight = viewportSize.height;
    const naturalWidth = imageNaturalSize.width;
    const naturalHeight = imageNaturalSize.height;

    if (viewportWidth <= 0 || viewportHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }

    // Keep intrinsic SVG size at zoom=1 so users can pan/zoom from true scale.
    const width = naturalWidth;
    const height = naturalHeight;

    return {
      left: (viewportWidth - width) / 2,
      top: (viewportHeight - height) / 2,
      width,
      height,
    };
  }, [viewportSize, imageNaturalSize]);

  const waypointDisplays = useMemo(
    () =>
      waypoints
        .filter((waypoint) => waypoint.x !== null && waypoint.y !== null)
        .map((waypoint) => {
          if (
            imageNaturalSize.width <= 0 ||
            imageNaturalSize.height <= 0 ||
            baseRect.width <= 0 ||
            baseRect.height <= 0 ||
            waypoint.x === null ||
            waypoint.y === null
          ) {
            return null;
          }

          return {
            ...waypoint,
            displayX: (waypoint.x / imageNaturalSize.width) * baseRect.width,
            displayY: (waypoint.y / imageNaturalSize.height) * baseRect.height,
          };
        })
        .filter((item): item is Waypoint & { displayX: number; displayY: number } => Boolean(item)),
    [waypoints, imageNaturalSize, baseRect],
  );

  const groupWaypointDisplays = useMemo(
    () =>
      groupWaypoints
        .filter((waypoint) => waypoint.x !== null && waypoint.y !== null)
        .map((waypoint) => {
          if (
            imageNaturalSize.width <= 0 ||
            imageNaturalSize.height <= 0 ||
            baseRect.width <= 0 ||
            baseRect.height <= 0 ||
            waypoint.x === null ||
            waypoint.y === null
          ) {
            return null;
          }

          return {
            ...waypoint,
            displayX: (waypoint.x / imageNaturalSize.width) * baseRect.width,
            displayY: (waypoint.y / imageNaturalSize.height) * baseRect.height,
          };
        })
        .filter((item): item is GroupWaypoint & { displayX: number; displayY: number } =>
          Boolean(item),
        ),
    [groupWaypoints, imageNaturalSize, baseRect],
  );

  const adaptiveZoomStep = useMemo(() => {
    const viewportWidth = Math.max(viewportSize.width, 1);
    const viewportHeight = Math.max(viewportSize.height, 1);
    const imageRatio = Math.max(
      imageNaturalSize.width / viewportWidth,
      imageNaturalSize.height / viewportHeight,
      1,
    );

    // Large images get a smaller zoom increment for smoother control.
    return clamp(zoomSpeed / Math.sqrt(imageRatio), 0.02, zoomSpeed);
  }, [imageNaturalSize, viewportSize, zoomSpeed]);

  useEffect(() => {
    if (!onViewportCenterChange) {
      return;
    }

    if (
      baseRect.width <= 0 ||
      baseRect.height <= 0 ||
      imageNaturalSize.width <= 0 ||
      imageNaturalSize.height <= 0
    ) {
      return;
    }

    const viewportCenterX = viewportSize.width / 2;
    const viewportCenterY = viewportSize.height / 2;
    const localCenterX = (viewportCenterX - baseRect.left - offset.x) / Math.max(zoom, 0.0001);
    const localCenterY = (viewportCenterY - baseRect.top - offset.y) / Math.max(zoom, 0.0001);
    const clampedCenterX = clamp(localCenterX, 0, baseRect.width);
    const clampedCenterY = clamp(localCenterY, 0, baseRect.height);
    const naturalCenterX = (clampedCenterX / baseRect.width) * imageNaturalSize.width;
    const naturalCenterY = (clampedCenterY / baseRect.height) * imageNaturalSize.height;

    onViewportCenterChange({
      x: Math.round(naturalCenterX),
      y: Math.round(naturalCenterY),
    });
  }, [onViewportCenterChange, baseRect, imageNaturalSize, viewportSize, offset, zoom]);

  const handleZoomIn = () => {
    setZoom((previous) => clamp(previous + adaptiveZoomStep, minZoom, maxZoom));
  };

  const handleZoomOut = () => {
    setZoom((previous) => clamp(previous - adaptiveZoomStep, minZoom, maxZoom));
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (isInteractingWithControls) {
      return;
    }
    if (isControlInteractionSuppressed()) {
      return;
    }

    event.preventDefault();

    if (baseRect.width <= 0 || baseRect.height <= 0) {
      return;
    }

    const direction = event.deltaY < 0 ? 1 : -1;
    const wheelIntensity = clamp(Math.abs(event.deltaY) / 100, 0.35, 2.5);
    const zoomDelta = adaptiveZoomStep * wheelIntensity;
    const nextZoom = clamp(zoom + direction * zoomDelta, minZoom, maxZoom);

    if (nextZoom === zoom) {
      return;
    }

    const viewportRect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - viewportRect.left;
    const mouseY = event.clientY - viewportRect.top;
    const contentX = (mouseX - baseRect.left - offset.x) / zoom;
    const contentY = (mouseY - baseRect.top - offset.y) / zoom;

    setOffset({
      x: mouseX - baseRect.left - contentX * nextZoom,
      y: mouseY - baseRect.top - contentY * nextZoom,
    });
    setZoom(nextZoom);
  };

  const handleTogglePanning = () => {
    setIsPanningEnabled((previous) => !previous);
    setIsDragging(false);
  };

  const handleResetView = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (resizeSession || moveSession || rotationSession) {
      return;
    }

    if (isInteractingWithControls) {
      return;
    }
    if (isControlInteractionSuppressed()) {
      return;
    }

    if (!isPanningEnabled) {
      return;
    }

    setIsDragging(true);
    setDragStart({
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    });
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (rotationSession) {
      if (!viewportRef.current) {
        return;
      }

      const viewportRect = viewportRef.current.getBoundingClientRect();
      const localMouseX =
        (event.clientX - viewportRect.left - baseRect.left - offset.x) / Math.max(zoom, 0.0001);
      const localMouseY =
        (event.clientY - viewportRect.top - baseRect.top - offset.y) / Math.max(zoom, 0.0001);

      const currentAngle =
        (Math.atan2(localMouseY - rotationSession.centerY, localMouseX - rotationSession.centerX) *
          180) /
        Math.PI;
      const deltaAngle = currentAngle - rotationSession.startPointerAngle;

      const normalizedRotation = (value: number) => {
        let next = value;
        while (next > 180) {
          next -= 360;
        }
        while (next < -180) {
          next += 360;
        }
        return Math.round(next);
      };

      if (rotationSession.targetType === "waypoint" && onWaypointRotate) {
        const backgroundRotation = normalizedRotation(
          rotationSession.startBackgroundRotationDeg + deltaAngle,
        );
        const iconRotation = normalizedRotation(rotationSession.startIconRotationDeg + deltaAngle);
        const nameRotation = normalizedRotation(rotationSession.startNameRotationDeg + deltaAngle);

        if (rotationSession.target === "all") {
          onWaypointRotate(rotationSession.id, {
            backgroundRotationDeg: backgroundRotation,
            iconRotationDeg: iconRotation,
            nameRotationDeg: nameRotation,
          });
          return;
        }

        onWaypointRotate(rotationSession.id, {
          backgroundRotationDeg:
            rotationSession.target === "background"
              ? backgroundRotation
              : rotationSession.startBackgroundRotationDeg,
          iconRotationDeg:
            rotationSession.target === "icon" ? iconRotation : rotationSession.startIconRotationDeg,
          nameRotationDeg:
            rotationSession.target === "name" ? nameRotation : rotationSession.startNameRotationDeg,
        });
      }

      if (rotationSession.targetType === "group" && onGroupWaypointRotate) {
        const backgroundRotation = normalizedRotation(
          rotationSession.startBackgroundRotationDeg + deltaAngle,
        );
        const nameRotation = normalizedRotation(rotationSession.startNameRotationDeg + deltaAngle);

        if (rotationSession.target === "all") {
          onGroupWaypointRotate(rotationSession.id, {
            backgroundRotationDeg: backgroundRotation,
            nameRotationDeg: nameRotation,
          });
          return;
        }

        onGroupWaypointRotate(rotationSession.id, {
          backgroundRotationDeg:
            rotationSession.target === "background"
              ? backgroundRotation
              : rotationSession.startBackgroundRotationDeg,
          nameRotationDeg:
            rotationSession.target === "name" ? nameRotation : rotationSession.startNameRotationDeg,
        });
      }

      return;
    }

    if (resizeSession) {
      const deltaX = (event.clientX - resizeSession.startClientX) / Math.max(zoom, 0.0001);
      const deltaY = (event.clientY - resizeSession.startClientY) / Math.max(zoom, 0.0001);

      const startLeft = resizeSession.startCenterX - resizeSession.startWidth / 2;
      const startRight = resizeSession.startCenterX + resizeSession.startWidth / 2;
      const startTop = resizeSession.startCenterY - resizeSession.startHeight / 2;
      const startBottom = resizeSession.startCenterY + resizeSession.startHeight / 2;

      let nextLeft = startLeft;
      let nextRight = startRight;
      let nextTop = startTop;
      let nextBottom = startBottom;

      if (resizeSession.handle.includes("w")) {
        nextLeft = Math.min(startLeft + deltaX, startRight - MIN_RESIZE_SIZE);
      }
      if (resizeSession.handle.includes("e")) {
        nextRight = Math.max(startRight + deltaX, startLeft + MIN_RESIZE_SIZE);
      }
      if (resizeSession.handle.includes("n")) {
        nextTop = Math.min(startTop + deltaY, startBottom - MIN_RESIZE_SIZE);
      }
      if (resizeSession.handle.includes("s")) {
        nextBottom = Math.max(startBottom + deltaY, startTop + MIN_RESIZE_SIZE);
      }

      const width = Math.round(nextRight - nextLeft);
      const height = Math.round(nextBottom - nextTop);
      const x = Math.round((nextLeft + nextRight) / 2);
      const y = Math.round((nextTop + nextBottom) / 2);

      if (resizeSession.targetType === "waypoint" && onWaypointResize) {
        onWaypointResize(resizeSession.id, { width, height, x, y });
      }

      if (resizeSession.targetType === "group" && onGroupWaypointResize) {
        onGroupWaypointResize(resizeSession.id, { width, height, x, y });
      }

      return;
    }

    if (moveSession) {
      if (
        baseRect.width <= 0 ||
        baseRect.height <= 0 ||
        imageNaturalSize.width <= 0 ||
        imageNaturalSize.height <= 0
      ) {
        return;
      }

      const deltaX = (event.clientX - moveSession.startClientX) / Math.max(zoom, 0.0001);
      const deltaY = (event.clientY - moveSession.startClientY) / Math.max(zoom, 0.0001);
      const nextDisplayX = clamp(moveSession.startDisplayX + deltaX, 0, baseRect.width);
      const nextDisplayY = clamp(moveSession.startDisplayY + deltaY, 0, baseRect.height);
      const naturalX = Math.round((nextDisplayX / baseRect.width) * imageNaturalSize.width);
      const naturalY = Math.round((nextDisplayY / baseRect.height) * imageNaturalSize.height);

      if (moveSession.targetType === "waypoint" && onWaypointMove) {
        onWaypointMove(moveSession.id, { x: naturalX, y: naturalY });
      }

      if (moveSession.targetType === "group" && onGroupWaypointMove) {
        onGroupWaypointMove(moveSession.id, { x: naturalX, y: naturalY });
      }

      return;
    }

    if (isInteractingWithControls) {
      return;
    }
    if (isControlInteractionSuppressed()) {
      return;
    }

    if (!isPanningEnabled || !isDragging) {
      return;
    }

    setOffset({
      x: event.clientX - dragStart.x,
      y: event.clientY - dragStart.y,
    });
  };

  const stopDragging = () => {
    if (resizeSession || moveSession || rotationSession) {
      markControlInteraction(150);
    }
    setIsDragging(false);
    setResizeSession(null);
    setMoveSession(null);
    setRotationSession(null);
  };

  const handlePinClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isInteractingWithControls) {
      return;
    }
    if (isControlInteractionSuppressed()) {
      return;
    }

    if (!isPinMode || isDragging || !onPinWaypoint || baseRect.width <= 0 || baseRect.height <= 0) {
      return;
    }

    const viewportRect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - viewportRect.left;
    const mouseY = event.clientY - viewportRect.top;

    const localX = (mouseX - baseRect.left - offset.x) / zoom;
    const localY = (mouseY - baseRect.top - offset.y) / zoom;

    const clampedLocalX = clamp(localX, 0, baseRect.width);
    const clampedLocalY = clamp(localY, 0, baseRect.height);

    const naturalX = (clampedLocalX / baseRect.width) * imageNaturalSize.width;
    const naturalY = (clampedLocalY / baseRect.height) * imageNaturalSize.height;

    onPinWaypoint({
      x: Math.round(naturalX),
      y: Math.round(naturalY),
    });
  };

  let cursor = "default";
  if (isPinMode) {
    cursor = "crosshair";
  }
  if (isPanningEnabled) {
    cursor = "grab";
  }
  if (isPanningEnabled && isDragging) {
    cursor = "grabbing";
  }

  if (resizeSession) {
    if (resizeSession.handle === "n" || resizeSession.handle === "s") {
      cursor = "ns-resize";
    }
    if (resizeSession.handle === "e" || resizeSession.handle === "w") {
      cursor = "ew-resize";
    }
    if (resizeSession.handle === "ne" || resizeSession.handle === "sw") {
      cursor = "nesw-resize";
    }
    if (resizeSession.handle === "nw" || resizeSession.handle === "se") {
      cursor = "nwse-resize";
    }
  }

  if (moveSession) {
    cursor = "grabbing";
  }

  if (rotationSession) {
    cursor = "grabbing";
  }

  const startResize = (
    event: React.MouseEvent<HTMLElement>,
    targetType: "waypoint" | "group",
    id: string,
    handle: ResizeHandle,
    centerX: number,
    centerY: number,
    width: number,
    height: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    markControlInteraction(150);
    setIsDragging(false);
    setResizeSession({
      targetType,
      id,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startCenterX: centerX,
      startCenterY: centerY,
      startWidth: width,
      startHeight: height,
    });
  };

  const startMove = (
    event: React.MouseEvent<HTMLElement>,
    targetType: "waypoint" | "group",
    id: string,
    displayX: number,
    displayY: number,
  ) => {
    if (isPinMode) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    markControlInteraction(150);
    setIsDragging(false);
    setResizeSession(null);
    setRotationSession(null);
    setMoveSession({
      targetType,
      id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDisplayX: displayX,
      startDisplayY: displayY,
    });
  };

  const startWaypointRotation = (
    event: React.MouseEvent<HTMLElement>,
    id: string,
    target: RotationTarget,
    centerX: number,
    centerY: number,
    backgroundRotationDeg: number,
    iconRotationDeg: number,
    nameRotationDeg: number,
  ) => {
    if (isPinMode || !viewportRef.current) {
      return;
    }

    const viewportRect = viewportRef.current.getBoundingClientRect();
    const localMouseX =
      (event.clientX - viewportRect.left - baseRect.left - offset.x) / Math.max(zoom, 0.0001);
    const localMouseY =
      (event.clientY - viewportRect.top - baseRect.top - offset.y) / Math.max(zoom, 0.0001);
    const startPointerAngle =
      (Math.atan2(localMouseY - centerY, localMouseX - centerX) * 180) / Math.PI;

    event.preventDefault();
    event.stopPropagation();
    markControlInteraction(150);
    setIsDragging(false);
    setResizeSession(null);
    setMoveSession(null);
    setRotationSession({
      targetType: "waypoint",
      id,
      target,
      centerX,
      centerY,
      startPointerAngle,
      startBackgroundRotationDeg: backgroundRotationDeg,
      startIconRotationDeg: iconRotationDeg,
      startNameRotationDeg: nameRotationDeg,
    });
  };

  const startGroupRotation = (
    event: React.MouseEvent<HTMLElement>,
    id: string,
    target: "all" | "background" | "name",
    centerX: number,
    centerY: number,
    backgroundRotationDeg: number,
    nameRotationDeg: number,
  ) => {
    if (isPinMode || !viewportRef.current) {
      return;
    }

    const viewportRect = viewportRef.current.getBoundingClientRect();
    const localMouseX =
      (event.clientX - viewportRect.left - baseRect.left - offset.x) / Math.max(zoom, 0.0001);
    const localMouseY =
      (event.clientY - viewportRect.top - baseRect.top - offset.y) / Math.max(zoom, 0.0001);
    const startPointerAngle =
      (Math.atan2(localMouseY - centerY, localMouseX - centerX) * 180) / Math.PI;

    event.preventDefault();
    event.stopPropagation();
    markControlInteraction(150);
    setIsDragging(false);
    setResizeSession(null);
    setMoveSession(null);
    setRotationSession({
      targetType: "group",
      id,
      target,
      centerX,
      centerY,
      startPointerAngle,
      startBackgroundRotationDeg: backgroundRotationDeg,
      startNameRotationDeg: nameRotationDeg,
    });
  };

  const resizeHandles: Array<{ handle: ResizeHandle; style: React.CSSProperties }> = [
    {
      handle: "n",
      style: { left: "50%", top: 0, transform: "translate(-50%, -50%)", cursor: "ns-resize" },
    },
    {
      handle: "s",
      style: { left: "50%", bottom: 0, transform: "translate(-50%, 50%)", cursor: "ns-resize" },
    },
    {
      handle: "e",
      style: { right: 0, top: "50%", transform: "translate(50%, -50%)", cursor: "ew-resize" },
    },
    {
      handle: "w",
      style: { left: 0, top: "50%", transform: "translate(-50%, -50%)", cursor: "ew-resize" },
    },
    {
      handle: "ne",
      style: { right: 0, top: 0, transform: "translate(50%, -50%)", cursor: "nesw-resize" },
    },
    {
      handle: "nw",
      style: { left: 0, top: 0, transform: "translate(-50%, -50%)", cursor: "nwse-resize" },
    },
    {
      handle: "se",
      style: { right: 0, bottom: 0, transform: "translate(50%, 50%)", cursor: "nwse-resize" },
    },
    {
      handle: "sw",
      style: { left: 0, bottom: 0, transform: "translate(-50%, 50%)", cursor: "nesw-resize" },
    },
  ];

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isPanningEnabled) {
      return;
    }

    const panStep = 20;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOffset((previous) => ({ ...previous, y: previous.y - panStep }));
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOffset((previous) => ({ ...previous, y: previous.y + panStep }));
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setOffset((previous) => ({ ...previous, x: previous.x - panStep }));
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      setOffset((previous) => ({ ...previous, x: previous.x + panStep }));
    }
  };

  return (
    <div>
      <div
        style={{
          width: "100%",
          height: "70vh",
          border: "1px solid #e8e8e8",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#f9fafb",
          position: "relative",
        }}
      >
        {showControls ? (
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 20,
              display: "flex",
              gap: 8,
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: 8,
                borderRadius: 10,
                backgroundColor: "rgba(20, 20, 20, 0.55)",
              }}
            >
              <Tooltip title="Zoom In" placement="right">
                <Button
                  type="text"
                  shape="circle"
                  onClick={handleZoomIn}
                  icon={<FontAwesomeIcon icon={faPlus} color="#f5f5f5" />}
                  aria-label="Zoom In"
                />
              </Tooltip>
              <Tooltip title="Zoom Out" placement="right">
                <Button
                  type="text"
                  shape="circle"
                  onClick={handleZoomOut}
                  icon={<FontAwesomeIcon icon={faMinus} color="#f5f5f5" />}
                  aria-label="Zoom Out"
                />
              </Tooltip>
              <Tooltip
                title={isPanningEnabled ? "Disable Panning" : "Enable Panning"}
                placement="right"
              >
                <Button
                  type="text"
                  shape="circle"
                  onClick={handleTogglePanning}
                  icon={
                    <FontAwesomeIcon
                      icon={faUpDown}
                      color={isPanningEnabled ? "#66d9ef" : "#f5f5f5"}
                    />
                  }
                  aria-label={isPanningEnabled ? "Disable Panning" : "Enable Panning"}
                />
              </Tooltip>
              <Tooltip title="Reset View" placement="right">
                <Button
                  type="text"
                  shape="circle"
                  onClick={handleResetView}
                  icon={<FontAwesomeIcon icon={faArrowsRotate} color="#f5f5f5" />}
                  aria-label="Reset View"
                />
              </Tooltip>
            </div>

            <div
              style={{
                padding: "10px 8px",
                borderRadius: 10,
                backgroundColor: "rgba(20, 20, 20, 0.55)",
                width: 56,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{ color: "#f5f5f5", fontSize: 11, fontWeight: 600, textAlign: "center" }}>
                Zoom Speed
              </div>
              <Slider
                vertical
                min={0.05}
                max={0.5}
                step={0.01}
                value={zoomSpeed}
                onChange={(value) => {
                  setIsInteractingWithControls(true);
                  markControlInteraction(300);
                  setZoomSpeed(typeof value === "number" ? value : zoomStep);
                }}
                onAfterChange={() => {
                  markControlInteraction(250);
                  setIsInteractingWithControls(false);
                }}
                tooltip={{ formatter: (value) => `${(value ?? 0).toFixed(2)}` }}
                style={{ height: 100, margin: 0 }}
              />
            </div>
          </div>
        ) : null}

        <div
          ref={viewportRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
          onClick={handlePinClick}
          role="button"
          tabIndex={0}
          aria-label="SVG controller viewport"
          style={{
            width: "100%",
            height: "100%",
            cursor,
            userSelect: "none",
            padding: 0,
            display: "block",
            border: "none",
            backgroundColor: "transparent",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: baseRect.left,
                top: baseRect.top,
                width: baseRect.width,
                height: baseRect.height,
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transformOrigin: "0 0",
              }}
            >
              <img
                src={svgSrc}
                alt={alt}
                draggable={false}
                onLoad={(event) => {
                  const image = event.currentTarget;
                  setImageNaturalSize({
                    width: image.naturalWidth || 1,
                    height: image.naturalHeight || 1,
                  });

                  if (viewportRef.current) {
                    const rect = viewportRef.current.getBoundingClientRect();
                    setViewportSize({ width: rect.width || 1, height: rect.height || 1 });
                  }

                  if (onSvgLoaded) {
                    onSvgLoaded();
                  }
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  border: "1px dashed #6b7280",
                  boxSizing: "border-box",
                  pointerEvents: "none",
                }}
              />
              {groupWaypointDisplays.map((waypoint) => {
                const isActiveGroup = isGroupEditorOpen && activeGroupWaypointId === waypoint.id;
                const groupBackgroundRotation = waypoint.backgroundRotationDeg ?? 0;
                const groupNameRotation = waypoint.nameRotationDeg ?? 0;
                const groupLabelFontSize = waypoint.fontSize || 14;
                const groupLabelClearance =
                  waypoint.displayName && waypoint.nickname
                    ? NAME_LABEL_GAP + groupLabelFontSize
                    : 0;
                const groupSyncRotations = waypoint.syncRotations ?? true;
                const groupRotationTargets = groupSyncRotations
                  ? [{ key: "all", label: "R" }]
                  : [
                      { key: "background", label: "B" },
                      { key: "name", label: "N" },
                    ];

                return (
                  <div key={waypoint.id}>
                    <button
                      type="button"
                      aria-label={`Edit group ${waypoint.id}`}
                      onClick={(event) => {
                        if (isControlInteractionSuppressed()) {
                          event.stopPropagation();
                          return;
                        }

                        event.stopPropagation();
                        if (onGroupWaypointSelect) {
                          onGroupWaypointSelect(waypoint.id);
                        }
                      }}
                      onMouseDown={(event) => {
                        if (!isActiveGroup) {
                          return;
                        }

                        startMove(
                          event,
                          "group",
                          waypoint.id,
                          waypoint.displayX,
                          waypoint.displayY,
                        );
                      }}
                      style={{
                        position: "absolute",
                        left: waypoint.displayX - waypoint.backgroundWidth / 2,
                        top: waypoint.displayY - waypoint.backgroundHeight / 2,
                        width: waypoint.backgroundWidth,
                        height: waypoint.backgroundHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1px dashed ${waypoint.backgroundColorHex}`,
                        borderRadius: 6,
                        backgroundColor: "transparent",
                        opacity: 1,
                        padding: 0,
                        cursor: isActiveGroup ? "move" : "pointer",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          width: waypoint.backgroundWidth,
                          height: waypoint.backgroundHeight,
                          border: `1px dashed ${waypoint.backgroundColorHex}`,
                          borderRadius: 6,
                          backgroundColor: waypoint.backgroundColorHex,
                          opacity: clamp(waypoint.backgroundOpacity / 100, 0, 1),
                          transform: `rotate(${groupBackgroundRotation}deg)`,
                          transformOrigin: "center center",
                        }}
                      />
                      {waypoint.displayName && waypoint.nickname && (
                        <span
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: `translate(-50%, -100%) rotate(${groupBackgroundRotation}deg) translateY(-${Math.round(
                              waypoint.backgroundHeight / 2 + NAME_LABEL_GAP,
                            )}px) rotate(${groupNameRotation - groupBackgroundRotation}deg)`,
                            transformOrigin: "center center",
                            whiteSpace: "nowrap",
                            fontSize: groupLabelFontSize,
                            lineHeight: 1,
                            display: "inline-flex",
                            alignItems: "flex-end",
                            color: waypoint.fontColorHex || "#000000",
                            fontWeight: 600,
                            textShadow: "0 1px 2px rgba(255,255,255,0.85)",
                            pointerEvents: "none",
                          }}
                        >
                          {waypoint.nickname}
                        </span>
                      )}
                    </button>

                    {isActiveGroup && (
                      <div
                        style={{
                          position: "absolute",
                          left: waypoint.displayX - waypoint.backgroundWidth / 2,
                          top: waypoint.displayY - waypoint.backgroundHeight / 2,
                          width: waypoint.backgroundWidth,
                          height: waypoint.backgroundHeight,
                          transform: `rotate(${groupBackgroundRotation}deg)`,
                          transformOrigin: "center center",
                          pointerEvents: "none",
                          zIndex: 8,
                        }}
                      >
                        {resizeHandles.map((anchor) => (
                          <span
                            key={`${waypoint.id}-${anchor.handle}`}
                            role="button"
                            tabIndex={-1}
                            aria-label={`Resize group ${waypoint.id} ${anchor.handle}`}
                            onMouseDown={(event) => {
                              startResize(
                                event,
                                "group",
                                waypoint.id,
                                anchor.handle,
                                waypoint.x ?? 0,
                                waypoint.y ?? 0,
                                waypoint.backgroundWidth,
                                waypoint.backgroundHeight,
                              );
                            }}
                            style={{
                              position: "absolute",
                              width: HANDLE_SIZE,
                              height: HANDLE_SIZE,
                              borderRadius: 999,
                              border: "2px solid #1677ff",
                              backgroundColor: "#ffffff",
                              boxSizing: "border-box",
                              pointerEvents: "auto",
                              ...anchor.style,
                            }}
                          />
                        ))}

                        {groupRotationTargets.map((target, index) => (
                          <button
                            key={`${waypoint.id}-group-rotate-${target.key}`}
                            type="button"
                            aria-label={`Rotate group ${waypoint.id} ${target.key}`}
                            onMouseDown={(event) => {
                              startGroupRotation(
                                event,
                                waypoint.id,
                                target.key as "all" | "background" | "name",
                                waypoint.displayX,
                                waypoint.displayY,
                                groupBackgroundRotation,
                                groupNameRotation,
                              );
                            }}
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: -(ROTATION_HANDLE_OFFSET + groupLabelClearance) - index * 18,
                              transform: "translate(-50%, -50%)",
                              width: HANDLE_SIZE + 2,
                              height: HANDLE_SIZE + 2,
                              borderRadius: 999,
                              border: "2px solid #16a34a",
                              backgroundColor: "#ffffff",
                              color: "#166534",
                              fontSize: 9,
                              fontWeight: 700,
                              lineHeight: `${HANDLE_SIZE - 2}px`,
                              textAlign: "center",
                              boxSizing: "border-box",
                              cursor: "grab",
                              zIndex: 9,
                              padding: 0,
                              pointerEvents: "auto",
                            }}
                          >
                            {target.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {waypointDisplays.map((waypoint) => {
                const isActiveWaypoint = isLiftEditorOpen && activeWaypointId === waypoint.id;
                const backgroundRotation = waypoint.backgroundRotationDeg ?? 0;
                const iconRotation = waypoint.iconRotationDeg ?? 0;
                const nameRotation = waypoint.nameRotationDeg ?? 0;
                const waypointLabelFontSize = waypoint.fontSize || 14;
                const waypointLabelClearance =
                  waypoint.displayName && waypoint.nickname
                    ? NAME_LABEL_GAP + waypointLabelFontSize
                    : 0;
                const iconSize = Math.round(
                  Math.min(waypoint.backgroundWidth, waypoint.backgroundHeight) * 0.8,
                );
                const syncRotations = waypoint.syncRotations ?? true;
                const waypointRotationTargets = syncRotations
                  ? [{ key: "all", label: "R" }]
                  : [
                      { key: "background", label: "B" },
                      { key: "icon", label: "I" },
                      { key: "name", label: "N" },
                    ];
                const waypointLiftType = resolveLiftType(waypoint.type);
                let waypointIconSrc = elClose;
                if (waypointLiftType === LiftType.ESCALATOR) {
                  waypointIconSrc = esStopped;
                } else if (waypointLiftType === LiftType.MOVINGWALK) {
                  waypointIconSrc = movingWalkStopped;
                }

                return (
                  <div key={waypoint.id}>
                    <button
                      type="button"
                      aria-label={`Edit waypoint ${waypoint.id}`}
                      onClick={(event) => {
                        if (isControlInteractionSuppressed()) {
                          event.stopPropagation();
                          return;
                        }

                        event.stopPropagation();
                        if (onWaypointSelect) {
                          onWaypointSelect(waypoint.id);
                        }
                      }}
                      onMouseDown={(event) => {
                        if (!isActiveWaypoint) {
                          return;
                        }

                        startMove(
                          event,
                          "waypoint",
                          waypoint.id,
                          waypoint.displayX,
                          waypoint.displayY,
                        );
                      }}
                      style={{
                        position: "absolute",
                        left: waypoint.displayX - waypoint.backgroundWidth / 2,
                        top: waypoint.displayY - waypoint.backgroundHeight / 2,
                        width: waypoint.backgroundWidth,
                        height: waypoint.backgroundHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        padding: 0,
                        background: "transparent",
                        cursor: isActiveWaypoint ? "move" : "pointer",
                      }}
                    >
                      {waypoint.displayName && waypoint.nickname && (
                        <span
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: `translate(-50%, -100%) rotate(${backgroundRotation}deg) translateY(-${Math.round(
                              waypoint.backgroundHeight / 2 + NAME_LABEL_GAP,
                            )}px) rotate(${nameRotation - backgroundRotation}deg)`,
                            transformOrigin: "center center",
                            whiteSpace: "nowrap",
                            fontSize: waypointLabelFontSize,
                            lineHeight: 1,
                            display: "inline-flex",
                            alignItems: "flex-end",
                            color: waypoint.fontColorHex || "#000000",
                            fontWeight: 600,
                            textShadow: "0 1px 2px rgba(255,255,255,0.85)",
                            pointerEvents: "none",
                          }}
                        >
                          {waypoint.nickname}
                        </span>
                      )}
                      <div
                        style={{
                          position: "absolute",
                          width: waypoint.backgroundWidth,
                          height: waypoint.backgroundHeight,
                          borderRadius: 4,
                          backgroundColor: waypoint.backgroundColorHex,
                          opacity: clamp(waypoint.backgroundOpacity / 100, 0, 1),
                          transform: `rotate(${backgroundRotation}deg)`,
                          transformOrigin: "center center",
                        }}
                      />
                      <img
                        src={waypointIconSrc}
                        alt="Lift waypoint"
                        style={{
                          width: iconSize,
                          height: iconSize,
                          objectFit: "contain",
                          display: "block",
                          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.45))",
                          position: "relative",
                          zIndex: 1,
                          transform: `rotate(${iconRotation}deg)`,
                          transformOrigin: "center center",
                        }}
                      />
                    </button>

                    {isLiftEditorOpen && activeWaypointId === waypoint.id && (
                      <div
                        style={{
                          position: "absolute",
                          left: waypoint.displayX - waypoint.backgroundWidth / 2,
                          top: waypoint.displayY - waypoint.backgroundHeight / 2,
                          width: waypoint.backgroundWidth,
                          height: waypoint.backgroundHeight,
                          transform: `rotate(${backgroundRotation}deg)`,
                          transformOrigin: "center center",
                          pointerEvents: "none",
                          zIndex: 8,
                        }}
                      >
                        {resizeHandles.map((anchor) => (
                          <span
                            key={`${waypoint.id}-${anchor.handle}`}
                            role="button"
                            tabIndex={-1}
                            aria-label={`Resize waypoint ${waypoint.id} ${anchor.handle}`}
                            onMouseDown={(event) => {
                              startResize(
                                event,
                                "waypoint",
                                waypoint.id,
                                anchor.handle,
                                waypoint.x ?? 0,
                                waypoint.y ?? 0,
                                waypoint.backgroundWidth,
                                waypoint.backgroundHeight,
                              );
                            }}
                            style={{
                              position: "absolute",
                              width: HANDLE_SIZE,
                              height: HANDLE_SIZE,
                              borderRadius: 999,
                              border: "2px solid #1677ff",
                              backgroundColor: "#ffffff",
                              boxSizing: "border-box",
                              pointerEvents: "auto",
                              ...anchor.style,
                            }}
                          />
                        ))}

                        {waypointRotationTargets.map((target, index) => (
                          <button
                            key={`${waypoint.id}-rotate-${target.key}`}
                            type="button"
                            aria-label={`Rotate waypoint ${waypoint.id} ${target.key}`}
                            onMouseDown={(event) => {
                              startWaypointRotation(
                                event,
                                waypoint.id,
                                target.key as RotationTarget,
                                waypoint.displayX,
                                waypoint.displayY,
                                backgroundRotation,
                                iconRotation,
                                nameRotation,
                              );
                            }}
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: -(ROTATION_HANDLE_OFFSET + waypointLabelClearance) - index * 18,
                              transform: "translate(-50%, -50%)",
                              width: HANDLE_SIZE + 2,
                              height: HANDLE_SIZE + 2,
                              borderRadius: 999,
                              border: "2px solid #16a34a",
                              backgroundColor: "#ffffff",
                              color: "#166534",
                              fontSize: 9,
                              fontWeight: 700,
                              lineHeight: `${HANDLE_SIZE - 2}px`,
                              textAlign: "center",
                              boxSizing: "border-box",
                              cursor: "grab",
                              zIndex: 9,
                              padding: 0,
                              pointerEvents: "auto",
                            }}
                          >
                            {target.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {renderOverlay
                ? renderOverlay({ width: baseRect.width, height: baseRect.height })
                : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SvgController;
